import { useState, useRef } from 'react'
import { profileService } from '../../services/profileService'
import WheelLoader from '../common/WheelLoader'
import type { Profile } from '../../types'

interface ResumeUploadProps {
  hasResume: boolean
  allowResumeDownload: boolean
  onUpdate: (profile: Profile) => void
}

const MAX_SIZE_MB = 10
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

export default function ResumeUpload({
  hasResume,
  allowResumeDownload,
  onUpdate,
}: ResumeUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input
    e.target.value = ''

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file')
      return
    }

    // Validate file size
    if (file.size > MAX_SIZE_BYTES) {
      setError(`Resume must be less than ${MAX_SIZE_MB}MB`)
      return
    }

    setError(null)
    setIsUploading(true)

    try {
      const updatedProfile = await profileService.uploadResume(file)
      onUpdate(updatedProfile)
    } catch {
      setError('Failed to upload resume. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = async () => {
    setError(null)
    setIsRemoving(true)

    try {
      const updatedProfile = await profileService.removeResume()
      onUpdate(updatedProfile)
    } catch {
      setError('Failed to remove resume. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  const handleToggleDownload = async () => {
    setError(null)
    setIsToggling(true)

    try {
      const updatedProfile = await profileService.updateProfile({
        allow_resume_download: !allowResumeDownload,
      })
      onUpdate(updatedProfile)
    } catch {
      setError('Failed to update setting. Please try again.')
    } finally {
      setIsToggling(false)
    }
  }

  const isLoading = isUploading || isRemoving || isToggling

  return (
    <div>
      <div className="space-y-4">
        {/* Upload Section */}
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                hasResume ? 'bg-green-100' : 'bg-gray-100'
              }`}
            >
              <svg
                className={`w-6 h-6 ${hasResume ? 'text-green-600' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>

          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {hasResume ? 'Resume uploaded' : 'No resume uploaded'}
            </p>
            <p className="text-xs text-gray-500">PDF format, max {MAX_SIZE_MB}MB</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClick}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <WheelLoader width={24} />
                  Uploading...
                </span>
              ) : hasResume ? (
                'Replace'
              ) : (
                'Upload'
              )}
            </button>

            {hasResume && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRemoving ? 'Removing...' : 'Remove'}
              </button>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Download Toggle - Only show if resume is uploaded */}
        {hasResume && (
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">Allow resume download</p>
              <p className="text-xs text-gray-500">
                Visitors can download your resume from your profile
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={allowResumeDownload}
              onClick={handleToggleDownload}
              disabled={isLoading}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                allowResumeDownload ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            >
              <span className="sr-only">Allow resume download</span>
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  allowResumeDownload ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  )
}
